import React, { useState, useEffect, useMemo } from 'react';
import Sidebar from '../components/Sidebar';
import { useMobile } from '../hooks/useMobile';

const API_TOKEN = () => localStorage.getItem('token');
const fmt = n => `£${parseFloat(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const SCOPE_CONFIG = {
  company: {
    label: 'Network Analytics',
    sub: 'Company-wide performance overview',
    gradient: 'linear-gradient(135deg, #0e1b2c 0%, #1a2f4e 55%, #3730a3 100%)',
    accent: '#818cf8',
    accentDark: '#4338ca',
    tag: 'COMPANY',
  },
  solo: {
    label: 'My Analytics',
    sub: 'Your performance overview',
    gradient: 'linear-gradient(135deg, #0e1b2c 0%, #1a2f4e 55%, #3730a3 100%)',
    accent: '#818cf8',
    accentDark: '#4338ca',
    tag: 'SOLO',
  },
  manager: {
    label: 'Team Analytics',
    sub: "Your team's performance",
    gradient: 'linear-gradient(135deg, #0e1b2c 0%, #1c3461 55%, #1d4ed8 100%)',
    accent: '#60a5fa',
    accentDark: '#2563eb',
    tag: 'MANAGER',
  },
  adviser: {
    label: 'My Performance',
    sub: 'Personal analytics dashboard',
    gradient: 'linear-gradient(135deg, #0e1b2c 0%, #134e4a 55%, #059669 100%)',
    accent: '#34d399',
    accentDark: '#059669',
    tag: 'ADVISER',
  },
};

const MEDALS = ['🥇', '🥈', '🥉'];

function flattenComm(commData) {
  const map = {};
  if (!commData) return map;
  const add = m => { if (m?.name) map[m.name] = m; };
  add(commData.company_owner);
  (commData.managers || []).forEach(m => { add(m); (m.advisers || []).forEach(add); });
  (commData.unassigned_advisers || []).forEach(add);
  return map;
}

// ─── Commission tree (company / manager view) ───────────────────────────────
function CommissionTree({ data, mobile }) {
  const [open, setOpen] = useState({});
  if (!data) return null;
  const { company_owner, managers, unassigned_advisers } = data;
  const toggle = id => setOpen(o => ({ ...o, [id]: !o[id] }));

  const Row = ({ m, indent, isManager, advisers }) => {
    const isOpen = open[m.id];
    const hasTeam = isManager && advisers?.length > 0;
    const alloc = m.total_allocated || 0;
    const paidPct = alloc > 0 ? Math.round((m.total_paid / alloc) * 100) : 0;
    return (
      <>
        <div
          onClick={() => hasTeam && toggle(m.id)}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '12px 14px', marginBottom: 6,
            background: indent === 0 ? '#f8fafc' : '#fff',
            border: `1px solid ${indent === 0 ? '#e2e8f0' : '#f1f5f9'}`,
            borderRadius: 10, marginLeft: indent * 24,
            cursor: hasTeam ? 'pointer' : 'default',
            flexWrap: mobile ? 'wrap' : 'nowrap',
          }}
        >
          <div style={{ width: 18, flexShrink: 0, color: '#94a3b8', fontSize: 12 }}>
            {hasTeam ? (isOpen ? '▾' : '▸') : '·'}
          </div>
          <div style={{
            width: 34, height: 34, borderRadius: '50%',
            background: 'linear-gradient(135deg, #0e1b2c, #1e3a5f)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontWeight: 700, fontSize: 13, flexShrink: 0,
          }}>
            {m.name?.charAt(0).toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#0e1b2c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {m.name}
              {hasTeam && <span style={{ marginLeft: 6, fontSize: 11, color: '#94a3b8' }}>({advisers.length} adviser{advisers.length !== 1 ? 's' : ''})</span>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
              <span style={{ fontSize: 10, fontWeight: 600, textTransform: 'capitalize', letterSpacing: '0.04em', color: '#64748b' }}>{m.introducer_level}</span>
              {alloc > 0 && (
                <div style={{ flex: 1, maxWidth: 80, height: 4, background: '#e2e8f0', borderRadius: 99, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${paidPct}%`, background: '#10b981', borderRadius: 99 }} />
                </div>
              )}
              {alloc > 0 && <span style={{ fontSize: 10, color: '#94a3b8' }}>{paidPct}% paid</span>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 14, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#065f46' }}>{fmt(m.total_paid)}</div>
              <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase' }}>Paid</div>
            </div>
            {m.payable > 0 && (
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>{fmt(m.payable)}</div>
                <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase' }}>Due</div>
              </div>
            )}
            {m.pipeline > 0 && (
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1e40af' }}>{fmt(m.pipeline)}</div>
                <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase' }}>Pipeline</div>
              </div>
            )}
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>{m.deal_count || 0}</div>
              <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase' }}>Deals</div>
            </div>
          </div>
        </div>
        {hasTeam && isOpen && advisers.map(a => (
          <Row key={a.id} m={a} indent={1} isManager={false} advisers={[]} />
        ))}
      </>
    );
  };

  const hasData = [company_owner, ...(managers || []), ...(unassigned_advisers || [])].some(m => m);
  if (!hasData) return <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: 13, background: '#f8fafc', borderRadius: 10, border: '1px dashed #e2e8f0' }}>No commission data yet</div>;

  return (
    <div>
      {company_owner && <Row m={company_owner} indent={0} isManager={false} advisers={[]} />}
      {(managers || []).map(mgr => <Row key={mgr.id} m={mgr} indent={0} isManager advisers={mgr.advisers || []} />)}
      {(unassigned_advisers || []).map(a => <Row key={a.id} m={a} indent={0} isManager={false} advisers={[]} />)}
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────
export default function IntroducerAnalyticsPage() {
  const mobile = useMobile();
  const [data, setData] = useState(null);
  const [commData, setCommData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const year = new Date().getFullYear();
  const [from, setFrom] = useState(`${year}-01-01`);
  const [to, setTo] = useState(`${year}-12-31`);
  const [months, setMonths] = useState(6);

  useEffect(() => {
    setLoading(true);
    setError('');
    const params = new URLSearchParams({ from, to, months });
    Promise.all([
      fetch(`/api/introducer/analytics?${params}`, { headers: { Authorization: `Bearer ${API_TOKEN()}` } }).then(r => r.json()),
      fetch('/api/introducer/team-commission', { headers: { Authorization: `Bearer ${API_TOKEN()}` } }).then(r => r.ok ? r.json() : null),
    ])
      .then(([a, c]) => { setData(a); setCommData(c); })
      .catch(() => setError('Failed to load analytics'))
      .finally(() => setLoading(false));
  }, [from, to, months]);

  const scope = data?.scope || 'adviser';
  const cfg = SCOPE_CONFIG[scope] || SCOPE_CONFIG.adviser;
  const isCompany = scope === 'company' || scope === 'solo';
  const isAdviser = scope === 'adviser';
  const ml = mobile ? 0 : 70;

  const commMap = useMemo(() => flattenComm(commData), [commData]);
  const totals = commData?.totals;

  const { leads, deals, conversionRate, monthlyLeads, topSubmitters } = data || {};
  const maxBar = Math.max(...(monthlyLeads || []).map(m => Math.max(m.leads, m.converted)), 1);

  const cardStyle = {
    background: '#fff',
    borderRadius: 16,
    border: '1px solid #e2e8f0',
    padding: mobile ? '20px' : '28px',
    marginBottom: 24,
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
  };

  if (loading) return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f1f5f9' }}>
      <Sidebar />
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: ml }}>
        <div style={{ textAlign: 'center', color: '#64748b' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>📊</div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>Loading analytics…</div>
        </div>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f1f5f9' }}>
      <Sidebar />
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: ml, color: '#ef4444', padding: 24 }}>{error}</div>
    </div>
  );

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f1f5f9' }}>
      <Sidebar />
      <div style={{ flex: 1, marginLeft: ml, minWidth: 0 }}>

        {/* ─── HERO BANNER ─── */}
        <div style={{
          background: cfg.gradient,
          padding: mobile ? '24px 16px 40px' : '32px 40px 56px',
          position: 'relative',
          overflow: 'hidden',
        }}>
          {/* Decorative background blobs */}
          <div style={{ position: 'absolute', top: -80, right: -80, width: 300, height: 300, borderRadius: '50%', background: 'rgba(255,255,255,0.03)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: -50, right: '20%', width: 180, height: 180, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', top: '30%', left: '60%', width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.02)', pointerEvents: 'none' }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            {/* Title + filters row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16, marginBottom: 28 }}>
              <div>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  background: 'rgba(255,255,255,0.1)', borderRadius: 99,
                  padding: '4px 12px', marginBottom: 10,
                }}>
                  <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase' }}>{cfg.tag}</span>
                  <span style={{ width: 4, height: 4, borderRadius: '50%', background: cfg.accent, display: 'inline-block' }} />
                  <span style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Dashboard</span>
                </div>
                <h1 style={{ margin: 0, fontSize: mobile ? 24 : 30, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
                  {cfg.label}
                </h1>
                <p style={{ margin: '6px 0 0', fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>{cfg.sub}</p>
              </div>

              {/* Date filters */}
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="date" value={from} onChange={e => setFrom(e.target.value)}
                  style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: 12, outline: 'none', colorScheme: 'dark' }}
                />
                <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: 14 }}>→</span>
                <input
                  type="date" value={to} onChange={e => setTo(e.target.value)}
                  style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: 12, outline: 'none', colorScheme: 'dark' }}
                />
                <select
                  value={months} onChange={e => setMonths(Number(e.target.value))}
                  style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: 12, outline: 'none' }}
                >
                  <option value={3}>3 months</option>
                  <option value={6}>6 months</option>
                  <option value={12}>12 months</option>
                </select>
              </div>
            </div>

            {/* Hero KPI cards (glassmorphism) */}
            <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr 1fr' : 'repeat(4,1fr)', gap: 12 }}>
              {[
                { icon: '📋', label: 'Leads Submitted', value: leads?.total || 0 },
                { icon: '🎯', label: 'Conversion Rate', value: `${conversionRate || 0}%` },
                { icon: '📊', label: isAdviser ? 'My Deals' : 'Total Deals', value: deals?.total || 0 },
                { icon: '💰', label: 'Commission Paid', value: fmt(totals?.total_paid || 0) },
              ].map(k => (
                <div key={k.label} style={{
                  background: 'rgba(255,255,255,0.07)',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 14,
                  padding: mobile ? '16px' : '22px',
                }}>
                  <div style={{ fontSize: 22, marginBottom: 10 }}>{k.icon}</div>
                  <div style={{ fontSize: mobile ? 22 : 28, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em', lineHeight: 1 }}>{k.value}</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 6, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>{k.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ─── MAIN CONTENT ─── */}
        <div style={{ padding: mobile ? '20px 16px' : '32px 40px', maxWidth: 1200 }}>

          {/* ─── LEAD FUNNEL ─── */}
          <div style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22, flexWrap: 'wrap', gap: 8 }}>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0e1b2c' }}>Lead Funnel</h2>
              <span style={{ fontSize: 12, color: '#64748b', background: '#f1f5f9', padding: '4px 12px', borderRadius: 99, fontWeight: 600 }}>
                {leads?.total || 0} total leads
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                { label: 'Submitted',          value: leads?.submitted, color: '#6366f1' },
                { label: 'Approved',           value: leads?.approved,  color: '#10b981' },
                { label: 'Converted to Deal',  value: leads?.converted, color: '#f59e0b' },
                { label: 'Rejected',           value: leads?.rejected,  color: '#ef4444' },
              ].map(f => {
                const pct = leads?.total > 0 ? Math.round(((f.value || 0) / leads.total) * 100) : 0;
                return (
                  <div key={f.label} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: mobile ? 90 : 140, fontSize: 13, fontWeight: 500, color: '#374151', flexShrink: 0 }}>{f.label}</div>
                    <div style={{ flex: 1, height: 32, background: '#f8fafc', borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                      <div style={{
                        height: '100%',
                        width: `${pct}%`,
                        background: f.color,
                        borderRadius: 7,
                        display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 8,
                        minWidth: pct > 0 ? 28 : 0,
                        transition: 'width 0.8s cubic-bezier(0.16,1,0.3,1)',
                      }}>
                        {pct > 6 && <span style={{ fontSize: 11, fontWeight: 700, color: '#fff' }}>{pct}%</span>}
                      </div>
                    </div>
                    <div style={{ width: 32, textAlign: 'right', fontSize: 16, fontWeight: 700, color: '#0e1b2c', flexShrink: 0 }}>{f.value || 0}</div>
                  </div>
                );
              })}
            </div>
            {leads?.total > 0 && (
              <div style={{ marginTop: 16, padding: '10px 14px', background: '#f0fdf4', borderRadius: 10, fontSize: 12, color: '#166534', border: '1px solid #bbf7d0' }}>
                💡 <strong>{conversionRate}%</strong> of your leads convert to deals —
                {' '}{conversionRate >= 50 ? 'excellent conversion rate!' : conversionRate >= 25 ? 'solid performance. Keep it up.' : 'focus on lead quality to improve conversions.'}
              </div>
            )}
          </div>

          {/* ─── MONTHLY PERFORMANCE CHART ─── */}
          <div style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 8 }}>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0e1b2c' }}>Monthly Performance</h2>
              <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#64748b' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 10, height: 10, background: '#6366f1', borderRadius: 3, display: 'inline-block' }} /> Leads
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 10, height: 10, background: '#10b981', borderRadius: 3, display: 'inline-block' }} /> Converted
                </span>
              </div>
            </div>
            {(!monthlyLeads || monthlyLeads.length === 0) ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', fontSize: 13, background: '#f8fafc', borderRadius: 10, border: '1px dashed #e2e8f0' }}>No data for this period</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: mobile ? 8 : 18, height: 180, minWidth: monthlyLeads.length * (mobile ? 56 : 76), paddingBottom: 0 }}>
                  {monthlyLeads.map(m => {
                    const leadsH = Math.max(4, Math.round((m.leads / maxBar) * 140));
                    const convH  = Math.max(4, Math.round((m.converted / maxBar) * 140));
                    return (
                      <div key={m.month} style={{ flex: 1, minWidth: mobile ? 48 : 60, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
                        <div style={{ display: 'flex', gap: 3, alignItems: 'flex-end', justifyContent: 'center', width: '100%' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            <span style={{ fontSize: 10, fontWeight: 700, color: '#6366f1' }}>{m.leads}</span>
                            <div style={{ width: mobile ? 14 : 18, background: '#6366f1', borderRadius: '4px 4px 0 0', height: leadsH + 'px', opacity: 0.85 }} />
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            <span style={{ fontSize: 10, fontWeight: 700, color: '#10b981' }}>{m.converted}</span>
                            <div style={{ width: mobile ? 14 : 18, background: '#10b981', borderRadius: '4px 4px 0 0', height: convH + 'px', opacity: 0.85 }} />
                          </div>
                        </div>
                        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 8, whiteSpace: 'nowrap', textAlign: 'center' }}>{m.month}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* ─── COMMISSION SECTION ─── */}
          {commData && (
            <div style={cardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22, flexWrap: 'wrap', gap: 10 }}>
                <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0e1b2c' }}>
                  {isAdviser ? 'My Commission' : 'Commission Breakdown'}
                </h2>
                {totals && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {[
                      { l: 'Paid',     v: fmt(totals.total_paid), c: '#065f46', bg: '#d1fae5' },
                      { l: 'Due',      v: fmt(totals.payable),    c: '#92400e', bg: '#fef3c7' },
                      { l: 'Pipeline', v: fmt(totals.pipeline),   c: '#1e40af', bg: '#dbeafe' },
                    ].map(t => (
                      <div key={t.l} style={{ background: t.bg, borderRadius: 8, padding: '5px 12px', textAlign: 'center' }}>
                        <div style={{ fontSize: 10, color: t.c, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t.l}</div>
                        <div style={{ fontSize: 16, fontWeight: 800, color: t.c }}>{t.v}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {isAdviser ? (
                /* Adviser: 3 big personal commission cards — no team data exposed */
                <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : 'repeat(3,1fr)', gap: 14 }}>
                  {[
                    { label: 'Paid to Me',  value: fmt(totals?.total_paid), sub: 'Commission received',  color: '#065f46', bg: 'linear-gradient(135deg, #d1fae5, #a7f3d0)', icon: '✅' },
                    { label: 'Due Now',     value: fmt(totals?.payable),    sub: 'Ready to be paid out', color: '#92400e', bg: 'linear-gradient(135deg, #fef3c7, #fde68a)', icon: '⏳' },
                    { label: 'In Pipeline', value: fmt(totals?.pipeline),   sub: 'Deals in progress',    color: '#1e40af', bg: 'linear-gradient(135deg, #dbeafe, #bfdbfe)', icon: '🔄' },
                  ].map(c => (
                    <div key={c.label} style={{ background: c.bg, borderRadius: 14, padding: '22px' }}>
                      <div style={{ fontSize: 26, marginBottom: 10 }}>{c.icon}</div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: c.color, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>{c.label}</div>
                      <div style={{ fontSize: mobile ? 26 : 32, fontWeight: 800, color: c.color, letterSpacing: '-0.02em', lineHeight: 1 }}>{c.value}</div>
                      <div style={{ fontSize: 11, color: c.color, opacity: 0.65, marginTop: 8 }}>{c.sub}</div>
                    </div>
                  ))}
                </div>
              ) : (
                /* Company / Manager: full team commission tree */
                <CommissionTree data={commData} mobile={mobile} />
              )}
            </div>
          )}

          {/* ─── LEADERBOARD (company / manager only) ─── */}
          {!isAdviser && topSubmitters && topSubmitters.length > 0 && (
            <div style={cardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22, flexWrap: 'wrap', gap: 8 }}>
                <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0e1b2c' }}>🏆 Performance Leaderboard</h2>
                <span style={{ fontSize: 12, color: '#64748b', background: '#f1f5f9', padding: '4px 12px', borderRadius: 99, fontWeight: 600 }}>Ranked by leads</span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {topSubmitters.map((person, i) => {
                  const commInfo = commMap[person.name];
                  const convPct  = person.lead_count > 0 ? Math.round((person.converted / person.lead_count) * 100) : 0;
                  const maxCount = topSubmitters[0]?.lead_count || 1;
                  const barPct   = maxCount > 0 ? Math.round((person.lead_count / maxCount) * 100) : 0;
                  const isTop    = i < 3;
                  const rowBg    = i === 0 ? 'linear-gradient(135deg,#fffbeb,#fef3c7)' : i === 1 ? 'linear-gradient(135deg,#f9fafb,#f1f5f9)' : i === 2 ? 'linear-gradient(135deg,#fff7ed,#ffedd5)' : '#fafafa';
                  const rowBorder= i === 0 ? '#fde68a' : i === 1 ? '#e2e8f0' : i === 2 ? '#fed7aa' : '#e2e8f0';
                  const convColor= convPct >= 50 ? '#065f46' : convPct >= 25 ? '#92400e' : '#374151';

                  return (
                    <div key={person.name + i} style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '14px 16px',
                      background: rowBg,
                      border: `1px solid ${rowBorder}`,
                      borderRadius: 12,
                      flexWrap: mobile ? 'wrap' : 'nowrap',
                    }}>
                      {/* Rank */}
                      <div style={{ width: 32, textAlign: 'center', flexShrink: 0, fontSize: isTop ? 22 : 14, fontWeight: 700, color: isTop ? undefined : '#94a3b8' }}>
                        {isTop ? MEDALS[i] : i + 1}
                      </div>
                      {/* Avatar */}
                      <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'linear-gradient(135deg,#0e1b2c,#1e3a5f)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 15, flexShrink: 0 }}>
                        {person.name?.charAt(0)?.toUpperCase()}
                      </div>
                      {/* Name + level */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#0e1b2c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{person.name}</div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: '#64748b', textTransform: 'capitalize', marginTop: 2 }}>{person.introducer_level}</div>
                      </div>
                      {/* Progress bar */}
                      {!mobile && (
                        <div style={{ flex: 2, maxWidth: 150 }}>
                          <div style={{ height: 6, background: '#e2e8f0', borderRadius: 99, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${barPct}%`, background: cfg.accentDark, borderRadius: 99 }} />
                          </div>
                        </div>
                      )}
                      {/* Stats */}
                      <div style={{ display: 'flex', gap: 16, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 18, fontWeight: 800, color: '#0e1b2c', lineHeight: 1 }}>{person.lead_count}</div>
                          <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', marginTop: 2 }}>Leads</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 18, fontWeight: 800, lineHeight: 1, color: convColor }}>{convPct}%</div>
                          <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', marginTop: 2 }}>Conv.</div>
                        </div>
                        {commInfo && commInfo.total_paid > 0 && (
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: 16, fontWeight: 800, color: '#065f46', lineHeight: 1 }}>{fmt(commInfo.total_paid)}</div>
                            <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', marginTop: 2 }}>Paid</div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ─── DEALS SUMMARY ─── */}
          <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr 1fr' : 'repeat(3,1fr)', gap: 16, marginBottom: 24 }}>
            {[
              { icon: '📈', label: 'Deals Created',   value: deals?.total || 0,     sub: 'In selected period', bg: '#eef2ff', color: '#4338ca' },
              { icon: '✅', label: 'Completed',        value: deals?.completed || 0,  sub: 'Fully completed',    bg: '#d1fae5', color: '#065f46' },
              { icon: '🎯', label: 'Completion Rate',  value: deals?.total > 0 ? `${Math.round((deals.completed / deals.total) * 100)}%` : '—', sub: 'Deal success rate', bg: '#fef3c7', color: '#92400e' },
            ].map(c => (
              <div key={c.label} style={{ background: c.bg, borderRadius: 14, padding: mobile ? '18px' : '24px' }}>
                <div style={{ fontSize: 24, marginBottom: 10 }}>{c.icon}</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: c.color, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>{c.label}</div>
                <div style={{ fontSize: mobile ? 28 : 36, fontWeight: 800, color: c.color, letterSpacing: '-0.02em', lineHeight: 1 }}>{c.value}</div>
                <div style={{ fontSize: 11, color: c.color, opacity: 0.6, marginTop: 8 }}>{c.sub}</div>
              </div>
            ))}
          </div>

        </div>
      </div>
    </div>
  );
}
